<template>
  <v-container fluid>
    <v-row>
      <v-col
        cols="4"
      >
        <v-autocomplete
          v-model="addressData.country_id"
          :items="optionsCountries"
          item-text="name"
          item-value="id"
          label="Country"
          outlined
          :error-messages="getErrorMessages(v$.addressData.country_id)"
          @input="v$.addressData.country_id.$touch()"
          @blur="v$.addressData.country_id.$touch()"
        />
      </v-col>
      <v-col
        cols="5"
      >
        <v-text-field
          v-model="addressData.address"
          autocomplete="off"
          label="Street Address"
          outlined
          :error-messages="getErrorMessages(v$.addressData.address)"
          @input="v$.addressData.address.$touch()"
          @blur="v$.addressData.address.$touch()"
        />
      </v-col>
      <v-col
        cols="3"
      >
        <v-text-field
          v-model="addressData.city"
          autocomplete="off"
          label="City"
          outlined
          :error-messages="getErrorMessages(v$.addressData.city)"
          @input="v$.addressData.city.$touch()"
          @blur="v$.addressData.city.$touch()"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-autocomplete
          v-model="addressData.state"
          :items="optionsStates"
          label="State"
          outlined
          :error-messages="getErrorMessages(v$.addressData.state)"
          @input="v$.addressData.state.$touch()"
          @blur="v$.addressData.state.$touch()"
        />
      </v-col>
      <v-col>
        <v-text-field
          v-model="addressData.zip"
          autocomplete="off"
          label="Zip"
          outlined
          :error-messages="getErrorMessages(v$.addressData.zip)"
          @input="v$.addressData.zip.$touch()"
          @blur="v$.addressData.zip.$touch()"
        />
      </v-col>
      <v-col>
        <v-text-field
          v-model="addressData.county"
          autocomplete="off"
          label="County"
          outlined
          :disabled="isZipableCountry || locked"
          :error-messages="getErrorMessages(v$.addressData.county)"
          @input="v$.addressData.county.$touch()"
          @blur="v$.addressData.county.$touch()"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { useVuelidate } from '@vuelidate/core'

import { SHARED_NAMESPACE } from '@/store/shared'

export default {
  name: 'ZipableAddressFields',

  model: {
    prop: 'address',
    event: 'change',
  },

  props: {
    address: {
      required: true,
      type: Object,
    },

    locked: {
      required: true,
      type: Boolean,
    },
  },

  setup () {
    return {
      v$: useVuelidate(),
    }
  },

  computed: {
    addressData () { return this.address },

    isZipableCountry () {
      return this.isCountryUSA()
    },

    optionsCountries () { return this.getCachedDropdown('countries') },
    optionsStates () { return this.getCachedDropdown('us_state/states') },

    ...mapGetters(SHARED_NAMESPACE, [
      'getCachedDropdown',
    ]),
  },

  watch: {
    'addressData.country_id': {
      handler: function (_) {
        this.onCountryChange()
      },

      immediate: true,
    },

    'addressData.zip': {
      handler: function (_) {
        this.onZipcodeChange()
      },

      immediate: true,
    },

    locked: {
      handler: function (newVal) {
        if (newVal === false) this.setCountryUSA()
      },

      immediate: true,
    },
  },

  validations () {
    return {
      addressData: {
        address: {},
        address_type: {},
        city: {},
        country_id: {},
        county: {},
        state: {},
        zip: {
          zipcode: this.zipcodeValidator,
        },
      },
    }
  },

  created () {
    this.getDropdownOptions({ path: 'us_state/states' })
    this.getDropdownOptions({ options: false, path: 'countries' })
  },

  methods: {
    getErrorMessages (validator) { return validator?.$errors.map(e => e.$message) },

    isCountryUSA () {
      if (!this.optionsCountries?.length) return

      const country = this.optionsCountries.find(c => c.id === this.addressData.country_id)
      return country?.name === 'USA'
    },

    onCountryChange () {
      this.v$.addressData?.zip.$touch()
      this.v$.addressData?.$validate()
    },

    onZipcodeChange () {
      const zipcode = this.addressData?.zip
      if (this.isCountryUSA() && zipcode) {
        this.lookupZipcode({ zipcode }).
          then(zipcodeInfo => {
            this.v$.addressData.county.$touch()
            this.addressData.county = zipcodeInfo?.county_name
          })
      }
    },

    setCountryUSA () {
      if (this.optionsCountries?.length > 0 && !this.addressData.country_id) {
        const country = this.optionsCountries.find(c => c.name === 'USA')
        this.v$.addressData?.country_id.$touch()
        this.addressData.country_id = country?.id
      }
    },

    zipcodeValidator () {
      if (!this.isCountryUSA()) return true

      const zipcode = this.addressData.zip
      if (!zipcode) return false

      return zipcode.match(/^[0-9]{5}$/)
    },

    ...mapActions(SHARED_NAMESPACE, [
      'getDropdownOptions',
      'lookupZipcode',
    ]),
  },
}
</script>
